import { ComponentType, lazy, Suspense } from "react";
import { BrowserRouter, Redirect, Switch } from "react-router-dom";

import { LoadingContainer } from "~components/LoadingContainer";
import { useAuth } from "~context/auth";
import { Login } from "~pages/Store/Login";
import { Profile } from "~pages/Store/Profile";
import { SellerAdminChatDock } from "~pages/SellerAdminChat/components/SellerAdminChatDock";
import { NotificationModal } from "../components/NotificationModal";
import { Route } from "./route";

// Lazy wrapper component
const LazyPage = ({ component: Component }: { component: ComponentType }) => (
  <Suspense fallback={<LoadingContainer content="a página" />}>
    <Component />
  </Suspense>
);

// ===========================================
// LAZY IMPORTS - Heavy pages (code splitting)
// ===========================================

// Admin Dashboard (heavy analytics)
const AdminDashboard = lazy(() =>
  import("../pages/AdminDashboard").then((m) => ({
    default: m.AdminDashboard,
  })),
);

// Commission Reports
const CommissionReports = lazy(() =>
  import("../pages/CommissionReports").then((m) => ({
    default: m.CommissionReports,
  })),
);

// Sales Performance
const SalesPerformance = lazy(() =>
  import("../pages/SalesPerformance/index").then((m) => ({
    default: m.SalesPerformance,
  })),
);

// Dindin Admin
const DindinAdmin = lazy(() =>
  import("../pages/DindinAdmin").then((m) => ({ default: m.DindinAdmin })),
);

// Cnae Enrichments
const CnaeEnrichments = lazy(() =>
  import("../pages/CnaeEnrichments").then((m) => ({
    default: m.CnaeEnrichments,
  })),
);

// Supplier Enrichment
const SupplierEnrichment = lazy(() =>
  import("../pages/SupplierEnrichments").then((m) => ({
    default: m.SupplierEnrichments,
  })),
);

// Opportunities (Sprint 3 - pipeline calibrado)
const Opportunities = lazy(() =>
  import("../pages/Opportunities").then((m) => ({
    default: m.Opportunities,
  })),
);

// Distribution Admin
const DistributionAdmin = lazy(() =>
  import("../pages/DistributionAdmin").then((m) => ({
    default: m.DistributionAdmin,
  })),
);

// Store - Products (heavy forms)
const Products = lazy(() =>
  import("~pages/Store/Products").then((m) => ({ default: m.Products })),
);
const ProductsDetails = lazy(() =>
  import("~pages/Store/Products/Details").then((m) => ({
    default: m.ProductsDetails,
  })),
);
const PriceAndAttributes = lazy(() =>
  import("~/pages/Store/Product/PriceAndAttributes").then((m) => ({
    default: m.PriceAndAttributes,
  })),
);
const Product = lazy(() =>
  import("~pages/Store/Product").then((m) => ({ default: m.Product })),
);
const RelatedProducts = lazy(() =>
  import("~pages/Store/Product/RelatedProducts").then((m) => ({
    default: m.RelatedProducts,
  })),
);
const SEO = lazy(() =>
  import("~pages/Store/Product/SEO").then((m) => ({ default: m.SEO })),
);
const StatePrices = lazy(() =>
  import("~pages/Store/Product/StatePrices").then((m) => ({
    default: m.StatePrices,
  })),
);
const ConfereLa = lazy(() =>
  import("~pages/Store/Product/ConfereLa").then((m) => ({
    default: m.ConfereLa,
  })),
);
const ProductLogs = lazy(() =>
  import("~pages/Store/Product/Logs").then((m) => ({
    default: m.ProductLogs,
  })),
);
const ViewProduct = lazy(() =>
  import("~pages/Store/ViewProduct").then((m) => ({ default: m.ViewProduct })),
);
const ViewProductByUrl = lazy(() =>
  import("~pages/Store/ViewProductByUrl").then((m) => ({
    default: m.ViewProductByUrl,
  })),
);

// Store - Import
const Import = lazy(() =>
  import("~pages/Store/Import").then((m) => ({ default: m.Import })),
);
const ProductImportV2 = lazy(() =>
  import("~pages/ProductImportV2").then((m) => ({ default: m.ProductImportV2 })),
);
const ProductImportV2History = lazy(() =>
  import("~pages/ProductImportV2/History").then((m) => ({ default: m.ProductImportV2History })),
);
const ProductImportV2Profiles = lazy(() =>
  import("~pages/ProductImportV2/Profiles").then((m) => ({ default: m.ProductImportV2Profiles })),
);
const NewImport = lazy(() =>
  import("~pages/Store/Import/New").then((m) => ({ default: m.NewImport })),
);
const ImportSystem = lazy(() =>
  import("~pages/Store/Import/Clients").then((m) => ({
    default: m.ImportSystem,
  })),
);
const ClientImportExternal = lazy(() => import("~pages/ClientImportExternal"));
const ImportStatePrices = lazy(() =>
  import("~pages/Store/Import/StatePrices").then((m) => ({
    default: m.ImportStatePrices,
  })),
);
const ImportTasks = lazy(() =>
  import("~pages/Store/Import/Tasks").then((m) => ({ default: m.ImportTasks })),
);
const ClientExportSystem = lazy(() =>
  import("../pages/Store/Export/Clients").then((m) => ({
    default: m.ClientExportSystem,
  })),
);
const ProductExport = lazy(() =>
  import("../pages/Store/Export/Products").then((m) => ({
    default: m.ProductExport,
  })),
);

// Store - Product Import
const ProductImport = lazy(() => import("../pages/ProductImport"));
const ProductImportDetail = lazy(() => import("../pages/ProductImport/Detail"));
const ImportProductImages = lazy(() => import("../pages/Store/Import/Images"));

// Store - Sales
const StoreSales = lazy(() =>
  import("~pages/Store/Sales").then((m) => ({ default: m.Sales })),
);
const NewStoreSale = lazy(() =>
  import("~pages/Store/Sales/New").then((m) => ({ default: m.NewSale })),
);

// Store - Categories, Brands, etc
const Categories = lazy(() =>
  import("~pages/Store/Categories").then((m) => ({ default: m.Categories })),
);
const Brands = lazy(() =>
  import("~pages/Store/Brands").then((m) => ({ default: m.Brands })),
);
const Attributes = lazy(() =>
  import("~pages/Store/Attributes").then((m) => ({ default: m.Attributes })),
);
const HighlightImages = lazy(() =>
  import("~pages/Store/HighlightImages").then((m) => ({
    default: m.HighlightImages,
  })),
);
const Suppliers = lazy(() =>
  import("~pages/Store/Suppliers").then((m) => ({ default: m.Suppliers })),
);
const Coupon = lazy(() =>
  import("~pages/Store/Coupon").then((m) => ({ default: m.Coupon })),
);

// Store - Tags (heavy)
const Tags = lazy(() =>
  import("../pages/Store/Tags").then((m) => ({ default: m.Tags })),
);
const TagDashboard = lazy(() => import("../pages/Tags/Dashboard"));

// Store - Landing Page (Page Builder)
const PageBuilder = lazy(() =>
  import("../pages/Store/LandingPage/PageBuilder/index").then((m) => ({
    default: m.PageBuilder,
  })),
);
const CategorySalesAudit = lazy(() =>
  import("../pages/Store/LandingPage/CategorySalesAudit/index"),
);
const ProductSalesAudit = lazy(() =>
  import("../pages/Store/LandingPage/ProductSalesAudit/index"),
);
const SupplierSalesAudit = lazy(() =>
  import("../pages/Store/LandingPage/SupplierSalesAudit/index"),
);
const ClientSalesAudit = lazy(() =>
  import("../pages/Store/LandingPage/ClientSalesAudit/index"),
);
const LocationSalesAudit = lazy(() =>
  import("../pages/Store/LandingPage/LocationSalesAudit/index"),
);

const ClientStorefronts = lazy(() =>
  import("../pages/Store/ClientStorefronts").then((m) => ({
    default: m.ClientStorefronts,
  })),
);

// Store - Cupons
const CupomAuge = lazy(() =>
  import("../pages/Cupom/CupomAuge").then((m) => ({ default: m.CupomAuge })),
);
const EditCupomAuge = lazy(() =>
  import("../pages/Cupom/Edit/EditCupom").then((m) => ({
    default: m.EditCupomAuge,
  })),
);

// Register - Clients V2
const ClientsV2 = lazy(() =>
  import("~pages/Register/ClientsV2").then((m) => ({ default: m.ClientsV2 })),
);

// BigDataCorp Central (Sprint 2)
const BigDataCorpCentral = lazy(() =>
  import("~pages/BigDataCorpCentral").then((m) => ({
    default: m.BigDataCorpCentral,
  })),
);

// Register - Clients (heavy forms)
const NewClient = lazy(() =>
  import("~pages/Register/Clients/New").then((m) => ({ default: m.NewClient })),
);
const ClientGroups = lazy(() =>
  import("~pages/Register/ClientGroups").then((m) => ({
    default: m.ClientGroups,
  })),
);
const NewClientGroup = lazy(() =>
  import("~pages/Register/ClientGroups/New").then((m) => ({
    default: m.NewClientGroup,
  })),
);
const ClientOrder = lazy(() =>
  import("~pages/Register/Clients/Order").then((m) => ({ default: m.Order })),
);
const ClientSupport = lazy(() =>
  import("~pages/Register/Clients/Support").then((m) => ({
    default: m.ClientSupport,
  })),
);
const EditSupport = lazy(() =>
  import("../pages/Register/Clients/Support/Edit").then((m) => ({
    default: m.EditSupport,
  })),
);
const ClientBasket = lazy(() =>
  import("~pages/Register/Clients/Basket").then((m) => ({ default: m.Basket })),
);
const ClientCommissionRuler = lazy(() =>
  import("~pages/Register/Clients/CommissionRuler").then((m) => ({
    default: m.ClientCommissionRuler,
  })),
);
const MostAccessed = lazy(() =>
  import("~pages/Register/Clients/MostAccessed").then((m) => ({
    default: m.MostAccessed,
  })),
);
const ClientLogs = lazy(() =>
  import("../pages/Register/Clients/Log/ClientLog").then((m) => ({
    default: m.ClientLogs,
  })),
);
const PersonalProducts = lazy(() =>
  import("../pages/Register/Clients/PersonalProducts").then((m) => ({
    default: m.PersonalProducts,
  })),
);
const ClientAttachmentsPage = lazy(() =>
  import("~pages/Register/Clients/Attachments").then((m) => ({
    default: m.ClientAttachmentsPage,
  })),
);
const ClientTasks = lazy(() =>
  import("~pages/Register/Clients/Tasks").then((m) => ({
    default: m.ClientTasks,
  })),
);

// Register - Buyers
const Buyer = lazy(() =>
  import("~pages/Register/Buyer").then((m) => ({ default: m.Buyer })),
);
const NewBuyer = lazy(() =>
  import("../pages/Register/Buyer/New").then((m) => ({ default: m.NewBuyer })),
);
const EditBuyer = lazy(() =>
  import("~pages/Register/Buyer/Edit").then((m) => ({ default: m.EditBuyer })),
);

// Register - Sellers
const Sellers = lazy(() =>
  import("../pages/Register/Sellers").then((m) => ({ default: m.Sellers })),
);
const EditSeller = lazy(() =>
  import("../pages/Register/Sellers/Edit").then((m) => ({
    default: m.EditSeller,
  })),
);

// Register - Suppliers (heavy)
const CurrentSuppliers = lazy(() =>
  import("~pages/Register/Supplier").then((m) => ({
    default: m.CurrentSuppliers,
  })),
);
const NewSupplier = lazy(() =>
  import("~pages/Register/Supplier/New").then((m) => ({
    default: m.NewSupplier,
  })),
);
const SuppliersRules = lazy(() =>
  import("~pages/Register/Supplier/Rules").then((m) => ({
    default: m.SuppliersRules,
  })),
);
const SupplierCheckoutRules = lazy(() =>
  import("~pages/Register/Supplier/CheckoutRules").then((m) => ({
    default: m.SupplierCheckoutRules,
  })),
);
const SuppliersCoupons = lazy(() =>
  import("~pages/Register/Supplier/Coupons").then((m) => ({
    default: m.SuppliersCoupons,
  })),
);
const SuppliersSales = lazy(() =>
  import("~pages/Register/Supplier/Sales").then((m) => ({ default: m.Salesx })),
);
const SupplierHybridFreights = lazy(() =>
  import("../pages/Register/Supplier/Freight").then((m) => ({
    default: m.SupplierHybridFreights,
  })),
);
const SupplierCommissionRuler = lazy(() =>
  import("../pages/Register/Supplier/CommissionRuler").then((m) => ({
    default: m.SupplierCommissionRuler,
  })),
);
const SuppliersMedia = lazy(() =>
  import("../pages/Register/Supplier/Media").then((m) => ({
    default: m.SuppliersMedia,
  })),
);
const SuppliersPrompts = lazy(() =>
  import("../pages/Register/Supplier/Prompts").then((m) => ({
    default: m.SuppliersPrompts,
  })),
);
const SuppliersCatalogs = lazy(() =>
  import("../pages/Register/Supplier/Catalogs").then((m) => ({
    default: m.SuppliersCatalogs,
  })),
);
const SuppliersTasks = lazy(() =>
  import("~pages/Register/Supplier/Tasks").then((m) => ({
    default: m.SuppliersTasks,
  })),
);
const SuppliersSupport = lazy(() =>
  import("~pages/Register/Supplier/Support").then((m) => ({
    default: m.SuppliersSupport,
  })),
);

// Register - Shipping
const Shipping = lazy(() =>
  import("../pages/Register/Shipping").then((m) => ({ default: m.Shipping })),
);
const NewShipping = lazy(() =>
  import("../pages/Register/Shipping/New").then((m) => ({
    default: m.NewShipping,
  })),
);

// Register - VCard
const VCard = lazy(() =>
  import("~pages/Register/vCard").then((m) => ({ default: m.VCard })),
);
const EditVCard = lazy(() =>
  import("~pages/Register/vCard/Edit").then((m) => ({ default: m.EditVCard })),
);

// Register - Banners
const Banners = lazy(() =>
  import("~pages/Register/Banner").then((m) => ({ default: m.Banners })),
);
const EditBanners = lazy(() =>
  import("../pages/Register/Banner/Edit").then((m) => ({
    default: m.EditBanners,
  })),
);
const BannerLibrary = lazy(() =>
  import("../pages/Register/BannerLibrary").then((m) => ({
    default: m.BannerLibrary,
  })),
);
const EditBannerLibrary = lazy(() =>
  import("../pages/Register/BannerLibrary/Edit").then((m) => ({
    default: m.EditBannerLibrary,
  })),
);
const BannerStats = lazy(() => import("../pages/Register/BannerLibrary/Stats"));

// Register - Admin
const Admin = lazy(() =>
  import("~pages/Register/Admin").then((m) => ({ default: m.Admin })),
);
const EditAdmin = lazy(() =>
  import("../pages/Register/Admin/Edit").then((m) => ({
    default: m.EditAdmin,
  })),
);

// Blog
const BlogPosts = lazy(() =>
  import("@/src/pages/Blog/Posts").then((m) => ({ default: m.BlogPosts })),
);
const BlogAuthor = lazy(() =>
  import("~pages/Blog/Author").then((m) => ({ default: m.BlogAuthor })),
);
const BlogCategory = lazy(() =>
  import("~pages/Blog/Category").then((m) => ({ default: m.BlogCategory })),
);
const Post = lazy(() =>
  import("~pages/Blog/Post").then((m) => ({ default: m.Post })),
);
const PostRelatedProducts = lazy(() =>
  import("~pages/Blog/Post/RelatedProducts").then((m) => ({
    default: m.PostRelatedProducts,
  })),
);
const PostSEO = lazy(() =>
  import("~pages/Blog/Post/SEO").then((m) => ({ default: m.PostSEO })),
);
const EditAuthor = lazy(() =>
  import("../pages/Blog/Author/Edit").then((m) => ({ default: m.EditAuthor })),
);

// Settings
const SiteInfo = lazy(() =>
  import("~pages/Settings/Info").then((m) => ({ default: m.SiteInfo })),
);
const SiteMap = lazy(() =>
  import("~pages/Settings/SiteMap").then((m) => ({ default: m.SiteMap })),
);
const EditSiteMap = lazy(() =>
  import("../pages/Settings/SiteMap/Edit").then((m) => ({
    default: m.EditSiteMap,
  })),
);
const ClientCatalogSettings = lazy(() =>
  import("~pages/Settings/ClientCatalog").then((m) => ({
    default: m.ClientCatalogSettings,
  })),
);
const ClientApiScripts = lazy(() =>
  import("~pages/Settings/ClientApiScripts").then((m) => ({
    default: m.ClientApiScripts,
  })),
);

// Finances (heavy)
const Finances = lazy(() =>
  import("../pages/Finances").then((m) => ({ default: m.Finances })),
);
const FinancesOrders = lazy(() =>
  import("../pages/Finances/Orders").then((m) => ({ default: m.Finances })),
);
const EditFinances = lazy(() =>
  import("~pages/Finances/Edit").then((m) => ({ default: m.EditFinances })),
);
const Invoices = lazy(() =>
  import("../pages/Finances/Invoices").then((m) => ({ default: m.Invoices })),
);
const NewInvoice = lazy(() =>
  import("../pages/Finances/NewInvoice").then((m) => ({
    default: m.NewInvoice,
  })),
);
const ImportXml = lazy(() =>
  import("../pages/Finances/ImportXml").then((m) => ({ default: m.ImportXml })),
);
const ImportExcelBilling = lazy(() =>
  import("../pages/Finances/ImportExcelBilling").then((m) => ({
    default: m.ImportExcelBilling,
  })),
);
const ImportExcelTitles = lazy(() =>
  import("../pages/Finances/ImportExcelTitles").then((m) => ({
    default: m.ImportExcelTitles,
  })),
);
const ReceiptAuthorization = lazy(() =>
  import("../pages/Finances/ReceiptAuthorization").then((m) => ({
    default: m.ReceiptAuthorization,
  })),
);
const TitlesManagement = lazy(() =>
  import("../pages/Finances/TitlesManagement").then((m) => ({
    default: m.TitlesManagement,
  })),
);
const InvoicesManagement = lazy(() =>
  import("../pages/Finances/InvoicesManagement").then((m) => ({
    default: m.InvoicesManagement,
  })),
);
const InvoiceBilletTags = lazy(() =>
  import("../pages/Finances/InvoiceBilletTags").then((m) => ({
    default: m.InvoiceBilletTags,
  })),
);
const InvoiceBilletTagTitulos = lazy(() =>
  import("../pages/Finances/InvoiceBilletTags/TagTitulosPage").then((m) => ({
    default: m.InvoiceBilletTagTitulos,
  })),
);

// Support
const Support = lazy(() =>
  import("~pages/Support").then((m) => ({ default: m.Support })),
);

// Tasks (general - all tasks)
const Tasks = lazy(() =>
  import("../pages/Tasks").then((m) => ({ default: m.Tasks })),
);
const TaskCategories = lazy(() =>
  import("../pages/TaskCategories").then((m) => ({
    default: m.TaskCategories,
  })),
);
const SupportReply = lazy(() =>
  import("../pages/Support/Reply").then((m) => ({ default: m.SupportReply })),
);

// Coupon Engine
const CouponEngine = lazy(() =>
  import("../pages/CouponEngine/index").then((m) => ({
    default: m.CouponEngine,
  })),
);

// Marketing Automation (already has its own routes)
const MarketingAutomationRoutes = lazy(() =>
  import("../pages/MarketingAutomation/routes").then((m) => ({
    default: m.MarketingAutomationRoutes,
  })),
);

// Lara IA (motor de mensagens WhatsApp via OpenAI)
const LaraIARoutes = lazy(() =>
  import("../pages/LaraIA/routes").then((m) => ({ default: m.LaraIARoutes })),
);

// WhatsApp Assistant (chatbot reativo)
const WhatsAppAssistantRoutes = lazy(() =>
  import("../pages/WhatsAppAssistant/routes").then((m) => ({
    default: m.WhatsAppAssistantRoutes,
  })),
);

// Gerenciador de Chat (supervisão admin)
const SellerClientChatRoutes = lazy(() =>
  import("../pages/SellerClientChat/routes").then((m) => ({
    default: m.SellerClientChatRoutes,
  })),
);

const SharedShowcase = lazy(() =>
  import("../pages/SharedShowcase").then((m) => ({
    default: m.SharedShowcase,
  })),
);

const MotorTriggers = lazy(() =>
  import("../pages/MotorTriggers").then((m) => ({
    default: m.MotorTriggers,
  })),
);
const MotorTriggerClients = lazy(() =>
  import("../pages/MotorTriggers").then((m) => ({
    default: m.MotorTriggerClients,
  })),
);

function AuthenticatedAdminChatDock() {
  const { signed } = useAuth();
  if (!signed) return null;
  return <SellerAdminChatDock />;
}

export default function Routes() {
  return (
    <BrowserRouter>
      <NotificationModal />
      <AuthenticatedAdminChatDock />
      <Switch>
        <Route path="/" exact component={Login} isPrivate={false} />
        <Route path="/profile" exact component={Profile} />
        <Route
          path="/dev/shared-showcase"
          exact
          render={() => <LazyPage component={SharedShowcase} />}
        />

        {/* Admin Dashboard */}
        <Route
          path="/admin-dashboard"
          exact
          render={() => <LazyPage component={AdminDashboard} />}
        />

        {/* Commission Reports */}
        <Route
          path="/commission-reports"
          exact
          render={() => <LazyPage component={CommissionReports} />}
        />

        {/* Sales & Analytics */}
        <Route
          path="/sales-performance"
          exact
          render={() => <LazyPage component={SalesPerformance} />}
        />
        <Route
          path="/dindin"
          exact
          render={() => <LazyPage component={DindinAdmin} />}
        />
        <Route
          path="/cnae-enrichments"
          exact
          render={() => <LazyPage component={CnaeEnrichments} />}
        />
        <Route
          path="/supplier-enrichment"
          exact
          render={() => <LazyPage component={SupplierEnrichment} />}
        />
        <Route
          path="/opportunities"
          exact
          render={() => <LazyPage component={Opportunities} />}
        />
        <Route
          path="/distribution-admin"
          exact
          render={() => <LazyPage component={DistributionAdmin} />}
        />
        <Route
          path="/motor/triggers/:id/clients"
          exact
          render={() => <LazyPage component={MotorTriggerClients} />}
        />
        <Route
          path="/motor/triggers"
          exact
          render={() => <LazyPage component={MotorTriggers} />}
        />

        {/* Store - Products */}
        <Route
          path="/store/products"
          exact
          render={() => <LazyPage component={Products} />}
        />
        <Route
          path="/store/products/details"
          render={() => <LazyPage component={ProductsDetails} />}
        />
        <Route
          path="/store/cupons"
          exact
          render={() => <LazyPage component={CupomAuge} />}
        />
        <Route
          path="/store/cupons/edit/:id"
          render={() => <LazyPage component={EditCupomAuge} />}
        />

        <Route
          path="/store/product"
          exact
          render={() => <LazyPage component={PriceAndAttributes} />}
        />
        <Route
          path="/store/product/view"
          render={() => <LazyPage component={ViewProduct} />}
        />
        <Route
          path="/store/product/attributes"
          render={() => <LazyPage component={Product} />}
        />
        <Route
          path="/store/product/seo"
          render={() => <LazyPage component={SEO} />}
        />
        <Route
          path="/store/product/related"
          render={() => <LazyPage component={RelatedProducts} />}
        />
        <Route
          path="/store/product/state-prices"
          render={() => <LazyPage component={StatePrices} />}
        />
        <Route
          path="/store/product/confere-la"
          render={() => <LazyPage component={ConfereLa} />}
        />
        <Route
          path="/store/product/logs"
          render={() => <LazyPage component={ProductLogs} />}
        />

        <Route
          path="/produtos/:slug"
          render={() => <LazyPage component={ViewProductByUrl} />}
        />

        {/* Store - Import/Export */}
        <Route
          path="/store/import"
          exact
          render={() => <LazyPage component={Import} />}
        />
        <Route
          path="/store/import/v2"
          exact
          render={() => <LazyPage component={ProductImportV2} />}
        />
        <Route
          path="/store/import/v2/history"
          exact
          render={() => <LazyPage component={ProductImportV2History} />}
        />
        <Route
          path="/store/import/v2/profiles"
          exact
          render={() => <LazyPage component={ProductImportV2Profiles} />}
        />
        <Route
          path="/store/import/v2/:id"
          exact
          render={() => <LazyPage component={ProductImportV2} />}
        />
        <Route
          path="/store/import/clients"
          exact
          render={() => <LazyPage component={ImportSystem} />}
        />
        <Route
          path="/store/import/clients/external"
          exact
          render={() => <LazyPage component={ClientImportExternal} />}
        />
        <Route
          path="/store/import/catalog"
          exact
          render={() => <LazyPage component={ProductImport} />}
        />
        <Route
          path="/store/import/catalog/:id"
          render={() => <LazyPage component={ProductImportDetail} />}
        />
        <Route
          path="/store/import/images"
          exact
          render={() => <LazyPage component={ImportProductImages} />}
        />
        <Route
          path="/store/import/state-prices"
          exact
          render={() => <LazyPage component={ImportStatePrices} />}
        />
        <Route
          path="/store/import/tasks"
          exact
          render={() => <LazyPage component={ImportTasks} />}
        />
        <Route
          path="/store/export/clients"
          exact
          render={() => <LazyPage component={ClientExportSystem} />}
        />
        <Route
          path="/store/export/products"
          exact
          render={() => <LazyPage component={ProductExport} />}
        />
        <Route
          path="/store/import/new"
          exact
          render={() => <LazyPage component={NewImport} />}
        />

        {/* Store - Sales & Coupons */}
        <Route
          path="/store/coupon"
          exact
          render={() => <LazyPage component={Coupon} />}
        />
        <Route
          path="/store/sales"
          exact
          render={() => <LazyPage component={StoreSales} />}
        />
        <Route
          path="/store/pedidos"
          exact
          render={() => <LazyPage component={StoreSales} />}
        />
        <Route
          path="/store/sales/new"
          render={() => <LazyPage component={NewStoreSale} />}
        />

        {/* Store - Catalog */}
        <Route
          path="/store/categories"
          render={() => <LazyPage component={Categories} />}
        />
        <Route
          path="/store/brands"
          render={() => <LazyPage component={Brands} />}
        />
        <Route
          path="/store/attributes"
          render={() => <LazyPage component={Attributes} />}
        />
        <Route
          path="/store/highlight_images"
          render={() => <LazyPage component={HighlightImages} />}
        />
        <Route
          path="/store/tags"
          render={() => <LazyPage component={Tags} />}
        />
        <Route
          path="/tags/dashboard"
          exact
          render={() => <LazyPage component={TagDashboard} />}
        />

        {/* Store - Landing Page (Page Builder) */}
        <Route
          path="/store/landing-page"
          exact
          render={() => <LazyPage component={PageBuilder} />}
        />
        <Route
          path="/store/client-storefronts"
          exact
          render={() => <LazyPage component={ClientStorefronts} />}
        />
        <Route
          path="/store/landing-page/sales-audit"
          exact
          render={() => (
            <Redirect to="/store/landing-page/sales-audit/categories" />
          )}
        />
        <Route
          path="/store/landing-page/sales-audit/categories"
          exact
          render={() => <LazyPage component={CategorySalesAudit} />}
        />
        <Route
          path="/store/landing-page/sales-audit/products"
          exact
          render={() => <LazyPage component={ProductSalesAudit} />}
        />
        <Route
          path="/store/landing-page/sales-audit/suppliers"
          exact
          render={() => <LazyPage component={SupplierSalesAudit} />}
        />
        <Route
          path="/store/landing-page/sales-audit/clients"
          exact
          render={() => <LazyPage component={ClientSalesAudit} />}
        />
        <Route
          path="/store/landing-page/sales-audit/locations"
          exact
          render={() => <LazyPage component={LocationSalesAudit} />}
        />
        <Route
          path="/store/suppliers"
          exact
          render={() => <LazyPage component={Suppliers} />}
        />

        {/* Register - Buyers */}
        <Route
          path="/register/buyers"
          exact
          render={() => <LazyPage component={Buyer} />}
        />
        <Route
          path="/register/buyers/new"
          render={() => <LazyPage component={NewBuyer} />}
        />
        <Route
          path="/register/buyers/edit"
          render={() => <LazyPage component={EditBuyer} />}
        />

        {/* Register - Clients V2 */}
        <Route
          path="/register/clients-v2"
          exact
          render={() => <LazyPage component={ClientsV2} />}
        />

        {/* BigDataCorp Central (Sprint 2) */}
        <Route
          path="/register/clients-v2/bigdatacorp"
          exact
          render={() => <LazyPage component={BigDataCorpCentral} />}
        />

        {/* Register - Clients */}
        <Route
          path="/register/clients"
          exact
          render={() => <Redirect to="/register/clients-v2" />}
        />
        <Route
          path="/register/clients/groups"
          exact
          render={() => <LazyPage component={ClientGroups} />}
        />
        <Route
          path="/register/sellers"
          exact
          render={() => <LazyPage component={Sellers} />}
        />
        <Route
          path="/register/sellers/edit/:id"
          render={() => <LazyPage component={EditSeller} />}
        />
        <Route
          path="/register/clients/new"
          render={() => <LazyPage component={NewClient} />}
        />
        <Route
          path="/register/clients/custom-products"
          render={() => <LazyPage component={PersonalProducts} />}
        />
        <Route
          path="/register/clients/groups/new"
          render={() => <LazyPage component={NewClientGroup} />}
        />
        <Route
          path="/register/clients/order"
          render={() => <LazyPage component={ClientOrder} />}
        />
        <Route
          path="/register/clients/support"
          exact
          render={() => <LazyPage component={ClientSupport} />}
        />
        <Route
          path="/register/clients/log"
          exact
          render={() => <LazyPage component={ClientLogs} />}
        />
        <Route
          path="/register/clients/support/edit"
          exact
          render={() => <LazyPage component={EditSupport} />}
        />
        <Route
          path="/register/clients/basket"
          render={() => <LazyPage component={ClientBasket} />}
        />
        <Route
          path="/register/clients/commission-ruler"
          render={() => <LazyPage component={ClientCommissionRuler} />}
        />
        <Route
          path="/register/clients/accessed"
          render={() => <LazyPage component={MostAccessed} />}
        />
        <Route
          path="/register/clients/attachments"
          render={() => <LazyPage component={ClientAttachmentsPage} />}
        />
        <Route
          path="/register/clients/tasks"
          render={() => <LazyPage component={ClientTasks} />}
        />

        {/* Register - Shipping */}
        <Route
          path="/register/shipping"
          exact
          render={() => <LazyPage component={Shipping} />}
        />
        <Route
          path="/register/shipping/new"
          render={() => <LazyPage component={NewShipping} />}
        />

        {/* Register - Suppliers */}
        <Route
          path="/register/suppliers/new"
          render={() => <LazyPage component={NewSupplier} />}
        />
        <Route
          path="/register/suppliers/all"
          render={() => <LazyPage component={CurrentSuppliers} />}
        />
        <Route
          path="/register/suppliers/rules"
          render={() => <LazyPage component={SuppliersRules} />}
        />
        <Route
          path="/register/suppliers/checkout"
          render={() => <LazyPage component={SupplierCheckoutRules} />}
        />
        <Route
          path="/register/suppliers/coupons"
          render={() => <LazyPage component={SuppliersCoupons} />}
        />
        <Route
          path="/register/suppliers/sales"
          render={() => <LazyPage component={SuppliersSales} />}
        />
        <Route
          path="/register/suppliers/freights"
          render={() => <LazyPage component={SupplierHybridFreights} />}
        />
        <Route
          path="/register/suppliers/commission-ruler"
          render={() => <LazyPage component={SupplierCommissionRuler} />}
        />
        <Route
          path="/register/suppliers/prompts"
          render={() => <LazyPage component={SuppliersPrompts} />}
        />
        <Route
          path="/register/suppliers/media"
          render={() => <LazyPage component={SuppliersMedia} />}
        />
        <Route
          path="/register/suppliers/catalogs"
          render={() => <LazyPage component={SuppliersCatalogs} />}
        />
        <Route
          path="/register/suppliers/tasks"
          render={() => <LazyPage component={SuppliersTasks} />}
        />
        <Route
          path="/register/suppliers/support"
          render={() => <LazyPage component={SuppliersSupport} />}
        />

        {/* Register - VCard */}
        <Route
          path="/register/vcard"
          exact
          render={() => <LazyPage component={VCard} />}
        />
        <Route
          path="/register/vcard/edit"
          render={() => <LazyPage component={EditVCard} />}
        />

        {/* Register - Banners */}
        <Route
          path="/register/banners"
          exact
          render={() => <LazyPage component={Banners} />}
        />
        <Route
          path="/register/banners/edit"
          render={() => <LazyPage component={EditBanners} />}
        />
        <Route
          path="/register/banner-library"
          exact
          render={() => <LazyPage component={BannerLibrary} />}
        />
        <Route
          path="/register/banner-library/new"
          render={() => <LazyPage component={EditBannerLibrary} />}
        />
        <Route
          path="/register/banner-library/edit/:id"
          render={() => <LazyPage component={EditBannerLibrary} />}
        />
        <Route
          path="/register/banner-library/stats/:id"
          render={() => <LazyPage component={BannerStats} />}
        />

        {/* Register - Admin */}
        <Route
          path="/register/admin"
          exact
          render={() => <LazyPage component={Admin} />}
        />
        <Route
          path="/register/admin/new"
          exact
          render={() => <LazyPage component={EditAdmin} />}
        />
        <Route
          path="/register/admin/edit/:id"
          exact
          render={() => <LazyPage component={EditAdmin} />}
        />

        {/* Blog */}
        <Route
          path="/blog/posts"
          render={() => <LazyPage component={BlogPosts} />}
        />
        <Route
          path="/blog/author"
          exact
          render={() => <LazyPage component={BlogAuthor} />}
        />
        <Route
          path="/blog/author/edit"
          render={() => <LazyPage component={EditAuthor} />}
        />
        <Route
          path="/blog/category"
          render={() => <LazyPage component={BlogCategory} />}
        />
        <Route
          path="/blog/post/edit"
          render={() => <LazyPage component={Post} />}
        />
        <Route
          path="/blog/post/seo"
          render={() => <LazyPage component={PostSEO} />}
        />
        <Route
          path="/blog/post/related"
          render={() => <LazyPage component={PostRelatedProducts} />}
        />

        {/* Settings */}
        <Route
          path="/settings/info"
          render={() => <LazyPage component={SiteInfo} />}
        />
        <Route
          path="/settings/map"
          exact
          render={() => <LazyPage component={SiteMap} />}
        />
        <Route
          path="/settings/map/edit"
          render={() => <LazyPage component={EditSiteMap} />}
        />
        <Route
          path="/settings/client-catalog"
          exact
          render={() => <LazyPage component={ClientCatalogSettings} />}
        />
        <Route
          path="/settings/client-api-scripts"
          exact
          render={() => <LazyPage component={ClientApiScripts} />}
        />

        {/* Finances */}
        <Route
          path="/finances"
          exact
          render={() => <LazyPage component={Finances} />}
        />
        <Route
          path="/finances/orders"
          exact
          render={() => <LazyPage component={FinancesOrders} />}
        />
        <Route
          path="/finances/edit/:id"
          render={() => <LazyPage component={EditFinances} />}
        />
        <Route
          path="/finances/invoices/:id"
          render={() => <LazyPage component={Invoices} />}
        />
        <Route
          path="/finances/new/:id"
          render={() => <LazyPage component={NewInvoice} />}
        />
        <Route
          path="/finances/invoice-billet-tags"
          exact
          render={() => <LazyPage component={InvoiceBilletTags} />}
        />
        <Route
          path="/finances/invoice-billet-tags/:tagId"
          exact
          render={() => <LazyPage component={InvoiceBilletTagTitulos} />}
        />
        <Route
          path="/finances/import-xml"
          exact
          render={() => <LazyPage component={ImportXml} />}
        />
        <Route
          path="/finances/import-excel-faturamento"
          exact
          render={() => <LazyPage component={ImportExcelBilling} />}
        />
        <Route
          path="/finances/import-excel-titulos"
          exact
          render={() => <LazyPage component={ImportExcelTitles} />}
        />
        <Route
          path="/finances/receipt-authorization"
          exact
          render={() => <LazyPage component={ReceiptAuthorization} />}
        />
        <Route
          path="/finances/titles-management"
          exact
          render={() => <LazyPage component={TitlesManagement} />}
        />
        <Route
          path="/finances/invoices-management"
          exact
          render={() => <LazyPage component={InvoicesManagement} />}
        />

        {/* Support */}
        <Route
          path="/support"
          exact
          render={() => <LazyPage component={Support} />}
        />

        {/* Tasks (general) */}
        <Route
          path="/tasks"
          exact
          render={() => <LazyPage component={Tasks} />}
        />
        <Route
          path="/tasks/categories"
          exact
          render={() => <LazyPage component={TaskCategories} />}
        />
        <Route
          path="/support/reply/:id"
          render={() => <LazyPage component={SupportReply} />}
        />

        {/* Coupon Engine */}
        <Route
          path="/coupon-engine"
          exact
          render={() => <LazyPage component={CouponEngine} />}
        />

        {/* Marketing Automation */}
        <Route
          path="/marketing"
          render={() => <LazyPage component={MarketingAutomationRoutes} />}
        />

        {/* Lara IA */}
        <Route
          path="/lara-ia"
          render={() => <LazyPage component={LaraIARoutes} />}
        />

        {/* WhatsApp Assistant */}
        <Route
          path="/whatsapp-assistant"
          render={() => <LazyPage component={WhatsAppAssistantRoutes} />}
        />

        {/* Gerenciador de Chat */}
        <Route
          path="/seller-client-chat"
          render={() => <LazyPage component={SellerClientChatRoutes} />}
        />
      </Switch>
    </BrowserRouter>
  );
}
